import { ActionGameFormVariantFunction } from '@/actions/gameForm/interfaces';
import Button from '@/components/Button';
import {
  FormControlData,
  FormControlText,
  FormControlTextType,
} from '@/components/FormControlText';
import FriendlyCaptcha from '@/components/FriendlyCaptcha';
import { GameFormVariant } from '@/components/GameFormVariant';
import { Icon, IconVariant } from '@/components/Icon';
import Modal from '@/components/Modal';
import ModalLoginInfo from '@/components/ModalLoginInfo';
import ModalPasswordForgetLinks from '@/components/ModalPasswordForgetLinks';
import { VALIDATIONS } from '@/config/forms/validations';
import pushDatalayer, { DataLayerEvent } from '@/utilities/pushDatalayer';
import setFocusIfElementExists from '@/utilities/setFocusIfElementExists';
import {
  getSubsidiaryMailFromId,
  subsidiaryConfig,
} from '@/utilities/subsidiaries';
import React, { useEffect, useState } from 'react';

export interface GameFormLoginProps {
  email: FormControlData;
  password: FormControlData;
  preselectedSubsidiary?: string;
  onSelect: any;
  isLoading: boolean;
  errorCode: number;
  disabled: boolean;
  isCaptchaEnabled: boolean;
  captchaRef: React.RefObject<FriendlyCaptcha | null>;
  callbackDone: (solution: string) => any;
  onChangeFormVariant: ActionGameFormVariantFunction;
  onChange: (id: string, value: string, isTouched?: boolean) => void;
}

let isInitialized = false;

const GameFormLogin: React.FC<React.PropsWithChildren<GameFormLoginProps>> = ({
  email,
  password,
  preselectedSubsidiary,
  onSelect,
  isLoading,
  disabled,
  errorCode,
  isCaptchaEnabled,
  captchaRef,
  callbackDone,
  onChangeFormVariant,
  onChange,
}) => {
  const onSubsidiaryChange = (event) => {
    setCurrentSubsidiary(event.target.value);
    onSelect(event);
  };

  const [currentSubsidiary, setCurrentSubsidiary] = useState('');
  useEffect(() => {
    if (!isInitialized) {
      setTimeout(() => {
        setFocusIfElementExists('#loginHeadline');
      }, 100);

      isInitialized = true;
    }

    if (!currentSubsidiary) {
      // initialer Aufruf?
      setCurrentSubsidiary(preselectedSubsidiary ? preselectedSubsidiary : '');
    }
  }, [currentSubsidiary, preselectedSubsidiary]);

  return (
    <div className="GameFormLogin">
      <div className="GameForm__container">
        <h1>Login / Anmeldung</h1>
        <h2 id="loginHeadline" className="Step__header h-flex--justify-start">
          <span>
            Melden Sie sich jetzt bei Ihrer
            Landes&shy;lotterie&shy;gesell&shy;schaft an{' '}
            <Button
              ariaLabel="Weitere Informationen zu Login beziehungsweise Anmeldung"
              onClick={() => Modal.open('modal-login-info')}
            >
              <Icon variant={IconVariant.Info} />
            </Button>
          </span>
        </h2>
        <div className="GameForm__required-info">
          <span className="GameForm__required">*</span> kennzeichnet
          Pflichtfelder
        </div>
      </div>
      <fieldset
        aria-label="Anmeldung bei Ihrer Landeslotteriegesellschaft"
        className="GameForm__container"
      >
        <legend className="visually-hidden">
          Benutzername, Passwort und Landeslotteriegesellschaft eingeben
        </legend>
        <div className="GameForm__group">
          <label className="GameForm__label" htmlFor="email">
            E-Mail Adresse<span className="GameForm__required">*</span>
          </label>

          <FormControlText
            id="email"
            name="email"
            type={FormControlTextType.Email}
            value={email.value}
            onChange={onChange}
            required={true}
            errorMessage={VALIDATIONS.email.message}
            isValid={email.isValid}
            isTouched={email.isTouched}
            autoComplete="email"
            errorId={email.isTouched && !email.isValid ? 'email-error' : ''}
          />
        </div>
        <div className="GameForm__group">
          <label className="GameForm__label" htmlFor="password">
            Passwort
          </label>
          <FormControlText
            id="password"
            name="passwort"
            autoComplete="current-password"
            type={FormControlTextType.Password}
            value={password.value}
            onChange={onChange}
            required={false}
            errorMessage={VALIDATIONS.password.message}
            isValid={password.isValid}
            isTouched={password.isTouched && password.value !== ''}
            errorId={
              password.isTouched && password.value !== '' && !password.isValid
                ? 'password-error'
                : ''
            }
          />

          <p>
            <a
              href="#"
              onClick={($event) => {
                $event.preventDefault();
                Modal.open('modal-password-forget');
              }}
            >
              Sie haben Ihr Passwort vergessen?
            </a>
          </p>
        </div>
        <div className="GameForm__group">
          <label className="GameForm__label" htmlFor="stateSelect">
            Ihr Bundesland
            <span className="GameForm__required">*</span>
          </label>

          <div className="GameForm__selectWrapper">
            <select
              value={currentSubsidiary}
              id="stateSelect"
              onChange={onSubsidiaryChange}
              className="GameForm__subsidiary-select"
            >
              <option value="" key="none" disabled>
                Bitte wählen Sie...
              </option>
              {subsidiaryConfig
                .sort((a, b) => {
                  return a.displayName.localeCompare(b.displayName);
                })
                .map((current) => {
                  return (
                    <option
                      value={getSubsidiaryMailFromId(current.id)}
                      key={current.id}
                    >
                      {current.displayName}
                    </option>
                  );
                })}
            </select>
            <Icon variant={IconVariant.ChevronDown} className="select-icon" />
          </div>
          {preselectedSubsidiary &&
            preselectedSubsidiary !== currentSubsidiary && (
              <p className="GameForm__hint">
                Wenn Sie das beim Ausfüllen des Spielscheins ausgewählte
                Bundesland anpassen, werden unter Umständen nicht sämtliche
                zuvor ausgewählte Spielfelder übertragen.
              </p>
            )}
        </div>

        {errorCode && (
          <div className="FormControlText__error-message h-spacer-medium-bottom">
            {errorCode === 404 || errorCode === 400
              ? 'Ihre Anmeldedaten sind nicht korrekt. Bitte überprüfen Sie die Schreibweise. ' +
                'Alternativ können Sie hier die Registrierung durchführen.'
              : errorCode === 998
                ? 'Es ist ein Verbindungsproblem aufgetreten. Bitte versuchen Sie es auf der Webseite der Lotteriegesellschaft Ihres Bundeslandes oder morgen erneut auf LOTTO.de.'
                : errorCode === 999
                  ? 'Die Sicherheitsprüfung ist fehlgeschlagen - bitte unten bei der Anti-Roboter-Verifizierung nochmal "Hier klicken" aktivieren.'
                  : 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es auf der Webseite der Lotteriegesellschaft Ihres Bundeslandes oder morgen erneut auf LOTTO.de'}
          </div>
        )}

        <Button
          type="submit"
          variant={Button.Variant.Loading}
          disabled={disabled || !email.isValid}
          isLoading={isLoading}
          fullWidth={true}
          id="login-button"
          onClick={() => {
            pushDatalayer(
              `click_element_name`,
              `Button - Anmelden`,
              DataLayerEvent.UserClick
            );
          }}
        >
          Anmelden
        </Button>

        <div className="h-separator">oder</div>

        <Button
          variant={Button.Variant.Secondary}
          disabled={isLoading}
          fullWidth={true}
          onClick={($event) => {
            $event.preventDefault();
            pushDatalayer(
              `click_element_name`,
              `Button - Ich habe noch kein Kundenkonto`,
              DataLayerEvent.UserClick
            );
            onChangeFormVariant(GameFormVariant.Register);
          }}
        >
          Ich habe noch kein Kundenkonto
        </Button>

        {isCaptchaEnabled && (
          <div className="GameForm__group">
            <h3 className="GameForm__label">Sicherheitsprüfung</h3>
            <FriendlyCaptcha
              ref={captchaRef}
              callbackDone={callbackDone}
            ></FriendlyCaptcha>
          </div>
        )}
      </fieldset>
      <ModalPasswordForgetLinks id="modal-password-forget" />
      <ModalLoginInfo id="modal-login-info" />
    </div>
  );
};

export default GameFormLogin;
